<template>
    <div>
        <div class="title">
            <h3 class="left">Att. comptable et sociale</h3>

            <template v-if="socialCertificateHasBeenFetched">
                <button v-if="!isEditing" 
                    @click="isEditing = true"
                    class="btn btn-default btn-sm"
                >
                    Modifier
                </button>
                
                <button v-else 
                    @click="isEditing = false"
                    class="btn btn-danger btn-sm"
                >
                    Annuler
                </button>
            </template>
        </div>

        <div class="content-wrapper">
            <div class="overlay" v-if="isLoading"></div>

            <div v-if="!isEditing">
                <p style="font-style: italic;">
                    Dernière mise à jour : 

                    <span v-if="lastUpdate">
                        <span v-if="lastUpdateDate" :class="{'text-red bold': updateRequired}">
                            le {{ lastUpdateDate | moment-date }}
                        </span>
                        <span v-if="lastUpdateUser">par {{ lastUpdateUser }}</span>
                    </span>

                    <span v-else class="text-danger">Jamais</span>
                </p>

                <!-- Customer data -->
                <table class="table table-hover dataTable no-footer" id="basicTable" role="grid">
                    <tbody>
                        <tr>
                            <td class="v-align-top">
                                <span>Comptabilité</span>
                            </td>

                            <td>
                                <ul v-if="accountingAddress">
                                    <li v-if="accountingAddress.corporation_name">
                                        {{ accountingAddress.corporation_name }}
                                    </li>
                                    <li v-if="accountingAddress.first_name || accountingAddress.last_name">
                                        {{ accountingAddress.first_name }} {{ accountingAddress.last_name }}
                                    </li>
                                    <li>
                                        {{ accountingAddress.street_line1 }}
                                    </li>
                                    <li v-if="accountingAddress.street_line2">
                                        {{ accountingAddress.street_line2 }}
                                    </li>
                                    <li>
                                        {{ accountingAddress.zipcode }} {{ accountingAddress.city }}
                                    </li>
                                    <li v-if="accountingAddress.decoded_state">
                                        {{ accountingAddress.decoded_state }}
                                    </li>
                                    <li v-if="accountingAddress.decoded_country">
                                        {{ accountingAddress.decoded_country }}
                                    </li>
                                </ul>

                                <p v-else class="text-danger">Non renseignée</p>
                            </td>
                        </tr>

                        <tr>
                            <td class="v-align-top">
                                <span>Documents sociaux</span>
                            </td>

                            <td>
                                <ul v-if="socialAddress">
                                    <li v-if="socialAddress.corporation_name">
                                        {{ socialAddress.corporation_name }}
                                    </li>
                                    <li v-if="socialAddress.first_name || socialAddress.last_name">
                                        {{ socialAddress.first_name }} {{ socialAddress.last_name }}
                                    </li>
                                    <li>
                                        {{ socialAddress.street_line1 }}
                                    </li>
                                    <li v-if="socialAddress.street_line2">
                                        {{ socialAddress.street_line2 }}
                                    </li>
                                    <li>
                                        {{ socialAddress.zipcode }} {{ socialAddress.city }}
                                    </li>
                                    <li v-if="socialAddress.decoded_state">
                                        {{ socialAddress.decoded_state }}
                                    </li>
                                    <li v-if="socialAddress.decoded_country">
                                        {{ socialAddress.decoded_country }}
                                    </li>
                                </ul>

                                <p v-else class="text-danger">Non renseignée</p>
                            </td>
                        </tr>

                        <tr>
                            <td class="v-align-top">
                                <span>
                                    N'emploie aucun salarié
                                </span>
                            </td>

                            <td>
                                <input type="checkbox" :checked="customerWithoutEmployee" onclick="return false;">
                            </td>
                        </tr>
                    </tbody>
                </table>
                <!-- /Customer data -->
            </div>

            <edition-form v-else
                :social-certificate="socialCertificate"
                @update-terminated="isEditing = false"
            >
            </edition-form>
        </div>
    </div>
</template>

<script>
    // import api from 'store/api'
    import { mapActions, mapState } from 'vuex'
    import EditionForm from './SocialCertificate/EditionForm'

    export default {
        components: {
            EditionForm
        },

        data() {
            return {
                isLoading: false,
                isEditing: false
            }
        },

        created() {
            this.isLoading = true

            this.fetchSocialCertificate()
                .then(() => this.isLoading = false)
        },

        computed: {
            ...mapState('customer', ['socialCertificate']),

            socialCertificateHasBeenFetched() {
                return Object.keys(this.socialCertificate).length > 0
            },

            accountingAddress() {
                if (this.socialCertificateHasBeenFetched && this.socialCertificate.hasOwnProperty('accounting_address')) {
                    return this.socialCertificate.accounting_address
                }

                return null
            },

            socialAddress() {
                if (this.socialCertificateHasBeenFetched && this.socialCertificate.hasOwnProperty('social_address')) {
                    return this.socialCertificate.social_address
                }

                return null
            },

            lastUpdate() {
                if (this.socialCertificateHasBeenFetched && this.socialCertificate.hasOwnProperty('last_update')) {
                    return this.socialCertificate.last_update
                }

                return null
            },

            lastUpdateDate() {
                return this.lastUpdate ? this.lastUpdate.created_at : null
            },

            lastUpdateUser() {
                return this.lastUpdate && this.lastUpdate.user ? this.lastUpdate.user.email : null
            },

            updateRequired() {
                if (!this.lastUpdateDate) {
                    return false
                }

                const lastUpdateDate = moment(this.lastUpdateDate)
                const aYearAgo = moment().subtract(1, 'year')

                return lastUpdateDate.isBefore(aYearAgo)
            },

            customerWithoutEmployee() {
                if (this.socialCertificateHasBeenFetched && this.socialCertificate.hasOwnProperty('customer')) {
                    return this.socialCertificate.customer.without_employee
                }

                return false
            }
        },

        methods: {
            ...mapActions('customer', ['fetchSocialCertificate'])
        }
    }
</script>

<style lang="scss" scoped>
    .content-wrapper {
        position: relative;
        
        .overlay {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(255, 255, 255, 0.5);
        }
    }

    .table {
        tbody {
            tr {
                td {
                    padding: 5px;
                    border: 0;
                    
                    &:first-child {
                        color: #AAA;
                    }

                    ul {
                        padding:0;
                        li {
                            list-style-type: none;
                        }
                    }
                }
            }

            tr.text-danger {
                td {
                    color: inherit;
                }
            }
        }
    }
</style>


// WEBPACK FOOTER //
// client/components/Customer/InformationsContent/SocialCertificate.vue